import {Alert, Backdrop, Box, CircularProgress} from '@mui/material';
import {t} from 'i18next';
import update from 'immutability-helper';
import {isEmpty, values} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {useConfiguration} from '../../../hooks/configuration';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {EventListResponse} from '../../../interfaces/Event';
import {
  SafeyeNode,
  SafeyeNodeListResponse,
} from '../../../interfaces/SafeyeNode';
import {useGetEventSummaryQuery} from '../../../redux/app/api/eventSummaryApi';
import reduxSelectors from '../../../redux/selectors';
import {getMsFromS} from '../../../utils/datetime';
import {
  OpenedSafeyeNode,
  OpenedSafeyeNodeMode,
} from '../../../utils/hazardAIPanel';
import ModalDraggable from '../../common/ModalDraggable';
import {
  usePanel,
  usePanelFilter,
} from '../../dashboards/entities/DashboardEntityContext';
import SafeyeNodeItemUpsert from '../../safeye-node/SafeyeNodeItemUpsert';
import {DashboardHistoryReportType} from '../DashboardPanelItem';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';
import EventsReport, {
  EventsReportData,
  getEventsReportData,
} from './EventsReport';
import {HazardAITabs} from './HazardAITabs';
import WarningHazardReport, {
  getWarningHazardsData,
} from './WarningHazardReport';

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value: DashboardPanelData) => void;
  onOpenHistory?: (
    id: number | string | number[],
    type: DashboardHistoryReportType
  ) => void;
}

interface HazardPanelData {
  viewType: 'warnings-hazards' | 'events';
  warningHazards: any; // TODO Make a proper type
  events: EventsReportData;
  /** Set to true when the user has changed this panel's date manually. */
  detachedFromDashboardDate?: boolean;
}

const getHazardPanelData = (): HazardPanelData => ({
  viewType: 'warnings-hazards',
  warningHazards: getWarningHazardsData(),
  events: getEventsReportData(),
});

export const HazardAI = ({value, onUpdate, onOpenHistory}: Props) => {
  // Constants
  const isDarkMode = useSelector(reduxSelectors.app.getIsDarkMode);

  // Panel config — includes detachedFromDashboardDate so it propagates via onUpdate
  const [config, setConfig] = useState<HazardPanelData>(
    !isEmpty(value)
      ? {
          ...(value as HazardPanelData),
          detachedFromDashboardDate: !!(value as HazardPanelData)
            .detachedFromDashboardDate,
        }
      : getHazardPanelData()
  );

  // Sync detach flag when the dashboard Apply with "re-attach" clears it from
  // outside (the entity is updated via handleEntityUpdate in DashboardItem).
  useEffect(() => {
    setConfig((prev) => ({
      ...prev,
      detachedFromDashboardDate: !!value?.detachedFromDashboardDate,
    }));
  }, [value?.detachedFromDashboardDate]);

  // Dashboard-wide date override: read the filter context and, when the panel
  // is not detached, use the dashboard dates in place of the panel's own dates.
  const {filter} = usePanelFilter();
  const effectiveDates = useMemo(
    () =>
      !config.detachedFromDashboardDate && filter?.params?.date_start
        ? {
            date_start: filter.params.date_start as string,
            date_end: filter.params.date_end as string,
          }
        : null,
    [
      config.detachedFromDashboardDate,
      filter?.params?.date_start,
      filter?.params?.date_end,
    ]
  );

  // Constants
  const safeyeNodesRefresh = useConfiguration(
    'hazard-ai',
    'hazard_ai_autorefresh_rate'
  );
  const safeyeNodesRefreshInterval = safeyeNodesRefresh?.value
    ? getMsFromS(+safeyeNodesRefresh?.value)
    : null;

  // Safeye Nodes
  const [fetchedSafeyeNodes, setFetchedSafeyeNodes] =
    useState<SafeyeNodeListResponse>();
  const [fetchedSafeyeNodesErrors, setFetchedSafeyeNodesErrors] = useState<
    string[]
  >([]);
  const [fetchedSafeyeNodesInProgress, setFetchedSafeyeNodesInProgress] =
    useState(false);

  const fetchSafeyeNodes = useCallback(async () => {
    setFetchedSafeyeNodesInProgress(true);
    setFetchedSafeyeNodesErrors([]);

    try {
      const params = {
        ...config.warningHazards.params,
        // Apply dashboard-wide date override when this panel is not detached.
        ...(effectiveDates ?? {}),
      };

      const resp = await API.get<SafeyeNodeListResponse>(
        `${apiBaseUrl}/safeye-nano-node`,
        {params}
      );
      setFetchedSafeyeNodes(resp?.data);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_f00af36a');
      setFetchedSafeyeNodesErrors(messages);
    }
    setFetchedSafeyeNodesInProgress(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.warningHazards.params, config.viewType, effectiveDates]);

  const toggleNoHeartbeatEvent = async (item: SafeyeNode) => {
    try {
      await API.patch<SafeyeNodeListResponse>(
        `${apiBaseUrl}/safeye-nano-node/${item.id}/no-heartbeat-event`
      );
      fetchSafeyeNodes();
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_f00af36b');
      setFetchedSafeyeNodesErrors(messages);
    }
  };

  useEffect(() => {
    fetchSafeyeNodes();
    onUpdate?.({...config});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.warningHazards.params, effectiveDates]);

  useEffect(() => {
    onUpdate?.({...config});
  }, [config.warningHazards.mapType, config.warningHazards.objectType]);

  useEffect(() => {
    // api call to reload tab contents
    onUpdate?.({...config});
    fetchEvents();
  }, [config.viewType]);

  useRefreshInterval(fetchSafeyeNodes, safeyeNodesRefreshInterval);

  // Opened Safeye Nodes
  const [openedSafeyeNodes, setOpenedSafeyeNodes] = useState<OpenedSafeyeNode>(
    {}
  );

  const handleOpenItem = (id: number, mode?: OpenedSafeyeNodeMode) => {
    if (!(id in openedSafeyeNodes)) {
      setOpenedSafeyeNodes({
        ...openedSafeyeNodes,
        [id]: {mode, id},
      });
    }
  };

  // Events
  const [fetchedEentsData, setFetchedEentsData] = useState<EventListResponse>();
  const [fetchedEventsErrors, setFetchedEventsErrors] = useState<string[]>([]);
  const [fetchedEventsInProgress, setFetchedEventsInProgress] = useState(false);

  const fetchEvents = useCallback(async () => {
    setFetchedEventsInProgress(true);
    setFetchedEventsErrors([]);
    try {
      const params = {
        ...config.events.params,
        // Apply dashboard-wide date override when this panel is not detached.
        ...(effectiveDates ?? {}),
        limit: config.viewType !== 'events' ? 1 : config.events.params.limit,
        product: ['hazard_ai'],
      };
      const resp = await API.get<EventListResponse>(`${apiBaseUrl}/event`, {
        params,
      });
      setFetchedEentsData(resp.data);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_098a3da9');
      setFetchedEventsErrors(messages);
    }
    setFetchedEventsInProgress(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.viewType, config.events.params, effectiveDates]);

  useEffect(() => {
    fetchEvents();
    onUpdate?.({...config});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.events.params, effectiveDates]);

  const eventRefresh = useConfiguration(
    'hazard-ai',
    'hazard_ai_event_autorefresh_rate'
  );
  const eventRefreshInterval = eventRefresh?.value
    ? getMsFromS(+eventRefresh?.value)
    : null;
  useRefreshInterval(fetchEvents, eventRefreshInterval);

  // Events Summary Data
  const {
    data: fetchedEventSummaryData,
    isFetching: fetchedEventSummaryInProgress,
    error: fetchedEventSummaryError,
    refetch: fetchEventSummary,
  } = useGetEventSummaryQuery();

  const fetchedEventSummaryErrors =
    fetchedEventSummaryError && 'data' in fetchedEventSummaryError
      ? (fetchedEventSummaryError.data as {errors?: string[]})?.errors ?? []
      : [];

  useEffect(() => {
    onUpdate?.({...config});
  }, [config]);

  const [panel] = usePanel();

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      overflow="hidden"
      id="hazard-ai-summary"
      bgcolor={isDarkMode ? 'background.default' : 'grey.100'}
    >
      <DashboardPanelTitleSlot>
        {t(`panels.${panel?.code}`)}
      </DashboardPanelTitleSlot>
      <Box>
        <HazardAITabs
          activeTab={config.viewType}
          objectType={config.warningHazards.objectType}
          detectionData={fetchedSafeyeNodes?.detection}
          isEventSummaryLoading={fetchedEventSummaryInProgress}
          isEventsLoading={fetchedEventsInProgress}
          onChangeActiveTab={(v) => {
            if (config.viewType !== v) {
              setConfig(
                update(config, {
                  viewType: {
                    $set: v,
                  },
                })
              );
            }
          }}
          onChangeObjectType={(v) => {
            setConfig(
              update(config, {
                warningHazards: {
                  objectType: {
                    $set: v,
                  },
                },
              })
            );
          }}
          totalEventsNumber={fetchedEventSummaryData?.hazard_ai?.count}
          unacknowledgedEventsNumber={
            fetchedEventSummaryData?.hazard_ai?.unacked
          }
        />
      </Box>

      <Box
        height="100%"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        position="relative"
        bgcolor={isDarkMode ? '#2E2E2E' : '#FFF'}
      >
        <Box>
          {fetchedSafeyeNodesErrors.map((error, idx) => (
            <Alert
              key={`error-c-${idx}`}
              severity="error"
              onClose={fetchSafeyeNodes}
            >
              {error}
            </Alert>
          ))}
          {fetchedEventsErrors.map((error, idx) => (
            <Alert
              key={`error-e-${idx}`}
              severity="error"
              onClose={fetchEvents}
            >
              {error}
            </Alert>
          ))}
          {fetchedEventSummaryErrors.map((error, idx) => (
            <Alert
              key={`error-e-${idx}`}
              severity="error"
              onClose={fetchEventSummary}
            >
              {error}
            </Alert>
          ))}
        </Box>
        <Box height="100%" overflow="hidden">
          {config.viewType === 'warnings-hazards' ? (
            <>
              <Backdrop
                open={fetchedSafeyeNodesInProgress}
                sx={{position: 'absolute', zIndex: 1199}}
              >
                <CircularProgress color="inherit" />
              </Backdrop>
              <WarningHazardReport
                value={
                  effectiveDates
                    ? {
                        ...config.warningHazards,
                        params: {
                          ...config.warningHazards.params,
                          ...effectiveDates,
                        },
                      }
                    : config.warningHazards
                }
                onOpenHistory={onOpenHistory}
                onRefresh={fetchSafeyeNodes}
                onToggleNoHeartbeatEvent={toggleNoHeartbeatEvent}
                isLoading={fetchedSafeyeNodesInProgress}
                fetchedData={fetchedSafeyeNodes}
                onChange={(v) => {
                  const newWarningHazards = v ?? getWarningHazardsData();
                  // Auto-detach: if the dashboard date was active and the user
                  // just changed the date in this panel, mark it as detached.
                  const dateChanged =
                    effectiveDates !== null &&
                    (newWarningHazards.params?.date_start !==
                      effectiveDates.date_start ||
                      newWarningHazards.params?.date_end !==
                        effectiveDates.date_end);
                  setConfig({
                    ...update(config, {
                      warningHazards: {$set: newWarningHazards},
                    }),
                    detachedFromDashboardDate: dateChanged
                      ? true
                      : config.detachedFromDashboardDate,
                  });
                }}
                onOpenItem={handleOpenItem}
              />
            </>
          ) : config.viewType === 'events' ? (
            <>
              <Backdrop open={false} sx={{position: 'absolute', zIndex: 1199}}>
                <CircularProgress color="inherit" />
              </Backdrop>
              <EventsReport
                value={
                  effectiveDates
                    ? {
                        ...config.events,
                        params: {
                          ...config.events.params,
                          ...effectiveDates,
                        },
                      }
                    : config.events
                }
                loading={fetchedEventsInProgress}
                fetchedData={fetchedEentsData}
                onRefresh={fetchEvents}
                onChange={(v) => {
                  const newEvents = v ?? getEventsReportData();
                  // Auto-detach: if the dashboard date was active and the user
                  // changed the date, mark this panel as detached.
                  const dateChanged =
                    effectiveDates !== null &&
                    (newEvents.params?.date_start !==
                      effectiveDates.date_start ||
                      newEvents.params?.date_end !== effectiveDates.date_end);
                  setConfig({
                    ...update(config, {
                      events: {$set: newEvents},
                    }),
                    detachedFromDashboardDate: dateChanged
                      ? true
                      : config.detachedFromDashboardDate,
                  });
                }}
              />
            </>
          ) : null}
        </Box>
      </Box>

      {values(openedSafeyeNodes).map(({id, mode}) => (
        <ModalDraggable key={`safeye-${id}`} open>
          <SafeyeNodeItemUpsert
            pk={id}
            mode={mode}
            onSubmitted={() => {
              fetchSafeyeNodes();
            }}
            onClose={() => {
              setOpenedSafeyeNodes(update(openedSafeyeNodes, {$unset: [id]}));
            }}
          />
        </ModalDraggable>
      ))}
    </Box>
  );
};
